import { Fragment, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRight,
  Award,
  Bell,
  BellOff,
  Building2,
  Calendar,
  CalendarClock,
  Contact,
  CreditCard,
  GraduationCap,
  Layers,
  Mail,
  MapPin,
  Megaphone,
  Phone,
  ShieldCheck,
  UserCheck,
  UserPlus,
  Users,
} from 'lucide-react';

import { Alert } from '@/components/ui/Alert';
import { Badge } from '@/components/ui/Badge';
import { SkeletonLines } from '@/components/ui/Skeleton';
import {
  SCHOOL_STATUS_LABEL,
  SCHOOL_STATUS_TONE,
  boardLabel,
  instituteLevelLabel,
} from '@/constants/institute';
import { useAuthStore } from '@/features/auth/store/auth.store';
import { useApiQuery } from '@/hooks/useApiQuery';
import { useIsClamped } from '@/hooks/useIsClamped';
import { formatCurrency, formatDate, formatNumber, formatTimeAgo } from '@/lib/format';
import { NOTICES_PATH, noticeAnchorId } from '@/features/institute/notices/constants';
import type { InstituteNoticeKind } from '@/features/institute/notices/schemas/instituteNotice.schema';
import type { InstituteNotice } from '@/types/instituteNotice';
import type { InstituteOverview } from '@/types/instituteDashboard';
import { cn, googleMapsSearchUrl } from '@/lib/utils';
import type { Permission } from '@/constants/permissions';
import { instituteApi } from '../api/institute.api';
import { QueryError } from '@/components/ui/QueryError';

/** An em dash wherever the caller's role does not reach the figure (§10). */
const NOT_MINE = '—';

/**
 * A figure that is also the way through to the records behind it.
 *
 * Given a destination it becomes a link; without one it stays a plain block. A
 * tile only earns a destination when two things are true: the figure actually
 * loaded, and the caller may open the screen it would land on. A number that
 * leads to a permission wall is worse than a number that leads nowhere, and a
 * dash is not something to click at all.
 *
 * One component rather than five conditionals, so the five tiles cannot start
 * disagreeing about what a clickable one looks like (CLAUDE.md §3).
 */
function MetricTile({
  to,
  label,
  className,
  children,
}: {
  to?: string;
  /** Read out in place of the tile's contents — "1,204, link" names no noun. */
  label: string;
  className: string;
  children: ReactNode;
}) {
  if (!to) return <div className={className}>{children}</div>;

  return (
    <Link
      to={to}
      aria-label={label}
      className={cn(
        className,
        // The cue that this tile is a target. A ring rather than a corner arrow:
        // every tile already carries a status pill in its top-right, and an icon
        // dropped there would either sit on top of it or be squeezed into the
        // padding. The ring is the console's blue, so one rule serves all five
        // whatever colour the tile itself is painted.
        'cursor-pointer hover:ring-2 hover:ring-inset hover:ring-blue-500/40',
        'focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-blue-500/60',
      )}
    >
      {children}
    </Link>
  );
}

/**
 * How each kind of notice is drawn in the updates panel.
 *
 * The three treatments are the design's own; which one a row gets is the
 * notice's `noticeType` rather than its position in the list, so the panel
 * cannot say "reminder" in amber one day and in purple the next.
 */
const UPDATE_LOOK: Record<InstituteNoticeKind, { Icon: typeof Users; className: string }> = {
  EVENT: {
    Icon: Users,
    className: 'bg-[#e1f9eb] dark:bg-emerald-950/60 text-[#10b981] dark:text-emerald-400',
  },
  REMINDER: {
    Icon: CreditCard,
    className: 'bg-[#fff2e2] dark:bg-amber-950/60 text-[#f97316] dark:text-amber-400',
  },
  GENERAL: {
    Icon: Megaphone,
    className: 'bg-[#f3ebff] dark:bg-purple-950/60 text-[#9333ea] dark:text-purple-400',
  },
};

/**
 * One update on the dashboard card, linking to the notice on the board.
 *
 * The title is held to two lines and the text to three. Where either is cut
 * short, a "Read more" arrow says so — an ellipsis alone does not tell anyone
 * the rest is one click away. Measured rather than guessed from the length,
 * because the same notice wraps differently on a phone and a wide screen.
 */
function ImportantUpdateRow({ update }: { update: InstituteNotice }) {
  const { Icon, className } = UPDATE_LOOK[update.noticeType];
  const formattedDate = update.noticeDate ? formatDate(update.noticeDate) : null;
  const hasBody = Boolean(update.body?.trim());
  const title = useIsClamped<HTMLHeadingElement>([update.title]);
  const body = useIsClamped<HTMLParagraphElement>([update.body]);
  const isCut = title.isClamped || (hasBody && body.isClamped);

  return (
    // Opens the board scrolled to this very notice.
    <Link
      to={`${NOTICES_PATH}#${noticeAnchorId(update.uuid)}`}
      aria-label={isCut ? `${update.title} — read the full notice` : undefined}
      className="group flex items-start justify-between gap-3 p-1.5 rounded-xl hover:bg-slate-50/60 dark:hover:bg-slate-800/30 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-accent-line"
    >
      <div className="flex items-start gap-3 min-w-0 flex-1">
        <span className={`grid h-10 w-10 shrink-0 place-items-center rounded-2xl ${className}`}>
          <Icon className="h-5 w-5" />
        </span>
        <div className="min-w-0 flex-1">
          <h3 ref={title.ref} className="text-xs sm:text-[13.5px] font-bold text-ink leading-snug line-clamp-2">
            {update.title}
          </h3>
          {(formattedDate || update.audienceLabel) && (
            <p className="text-xs mt-0.5 flex items-center gap-1.5 flex-wrap">
              {formattedDate && (
                <span className="font-medium text-ink-2">
                  {formattedDate}
                </span>
              )}
              {formattedDate && update.audienceLabel && (
                <span className="text-ink-3">·</span>
              )}
              {update.audienceLabel && (
                <span className="font-bold text-blue-500 dark:text-blue-400">
                  {update.audienceLabel}
                </span>
              )}
            </p>
          )}
          {hasBody && (
            <p
              ref={body.ref}
              className="text-[11px] font-medium text-ink-3 mt-1 line-clamp-3 leading-relaxed"
              title={update.body}
            >
              {update.body}
            </p>
          )}
          {isCut && (
            <span className="mt-1 inline-flex items-center gap-1 text-[11px] font-bold text-accent" aria-hidden>
              Read more
              <ArrowRight className="h-3 w-3 transition-transform group-hover:translate-x-0.5" />
            </span>
          )}
        </div>
      </div>
      <span className="text-[11px] font-medium text-ink-3 shrink-0 pt-0.5 whitespace-nowrap">
        {formatTimeAgo(update.createdAt)}
      </span>
    </Link>
  );
}

/**
 * The line under the welcome, built from the two figures it claims.
 *
 * A sentence about attendance and fees has to come from attendance and fees:
 * whatever it says is the first thing anybody reads on this screen, and a
 * fixed one would go on saying it through a week nobody took the register.
 */
function heroSummary(overview: InstituteOverview | null): string {
  if (!overview) return 'Your institute at a glance.';

  const clauses: string[] = [];
  const { students, fees } = overview;

  if (students) {
    const change = students.weekOnWeekChange;
    // "Steady" is a comparison, so it is said only when there was one to make
    // and it came out level. With no register taken today the rate is 0% of the
    // roll, which is not attendance at all — the sentence says so instead.
    if (students.marked === 0) {
      clauses.push('No attendance has been marked yet today');
    } else if (change === null) {
      clauses.push(`Attendance is ${students.attendanceRate}% today`);
    } else if (change === 0) {
      clauses.push(`Attendance is steady at ${students.attendanceRate}% today`);
    } else {
      // The change is the difference between two rates, so it is in percentage
      // points — "up 5%" would read as 5% of the rate, a much smaller move.
      const points = Math.abs(change);
      clauses.push(
        `Attendance is ${change > 0 ? 'up' : 'down'} ${points} ${points === 1 ? 'point' : 'points'} this week`,
      );
    }
  }

  if (fees) clauses.push(`${fees.collectionRate}% of fees are collected`);

  return clauses.length > 0 ? `${clauses.join(' and ')}.` : 'Your institute at a glance.';
}

/** A bar's fill, clamped — a figure over 100 must not be drawn off the end. */
function barWidth(rate: number | undefined): number {
  return Math.max(0, Math.min(100, rate ?? 0));
}

/**
 * The primary dashboard screen for the institute admin console.
 * Styled to match the executive design mock with photographic hero banner,
 * live operations summary card, and metrics stats row.
 */
export function InstituteDashboardPage() {
  const user = useAuthStore((state) => state.user);
  const canSeeProfile = Boolean(user?.permissions?.includes('INSTITUTE_PROFILE_VIEW'));

  const {
    data: institute,
    isLoading,
    error,
    refetch,
  } = useApiQuery(
    () => (canSeeProfile ? instituteApi.getProfile() : Promise.resolve(null)),
    [canSeeProfile],
  );

  /**
   * Every number on the screen, in one request beside the profile.
   *
   * Separate from the profile call rather than folded into it: what the
   * institute *is* barely changes, and what it did *today* changes all morning.
   */
  const {
    data: overview,
    isLoading: isOverviewLoading,
    error: overviewError,
    refetch: refetchOverview,
  } = useApiQuery(
    () => (canSeeProfile ? instituteApi.getOverview() : Promise.resolve(null)),
    [canSeeProfile],
  );

  /**
   * Where each of the five figures leads.
   *
   * `undefined` where the figure did not load or the caller may not open the
   * screen behind it — `MetricTile` then draws a plain block instead of a link.
   * The permissions are the ones the sidebar gates the same routes on
   * (`constants/navigation.ts`), so a tile can never offer a door the menu does
   * not.
   */
  const may = (permission: Permission) => Boolean(user?.permissions?.includes(permission));
  const linkIf = (hasRecord: boolean, permission: Permission, to: string) =>
    hasRecord && may(permission) ? to : undefined;

  const students = overview?.students ?? null;
  const staff = overview?.staff ?? null;
  // The day the server counted, in IST — not the browser's clock, which can be
  // in another time zone or left open past midnight.
  const countedOn = overview ? `Today, ${formatDate(overview.date)}` : null;
  const fees = overview?.fees ?? null;
  const updates = overview?.updates ?? [];

  // The register has no student-attendance screen of its own, so both the
  // present count and the rate lead to the roll — the children the figures are
  // about. Punch-ins are the staff equivalent and do have one.
  const studentsTo = linkIf(Boolean(students), 'STUDENT_VIEW', '/admin/students');
  const onDutyTo = linkIf(Boolean(staff), 'STAFF_ATTENDANCE_VIEW', '/admin/staff-attendance');
  const staffTo = linkIf(Boolean(staff), 'STAFF_VIEW', '/admin/staff');
  const feesTo = linkIf(Boolean(fees), 'FEE_VIEW', '/admin/fees');

  // One alert for the pair: whichever call failed, the reader is looking at the
  // same broken screen and has the same one thing to do about it.
  const loadError = error ?? overviewError;

  // Both, not either: the profile arrives first and drawing the cards against a
  // half-loaded screen would show every figure as a dash for a moment.
  const isBusy = isLoading || isOverviewLoading;

  const retryAll = () => {
    refetch();
    refetchOverview();
  };

  return (
    <div className="flex flex-col gap-6">
      {loadError && (
        <QueryError title="Could not load your institute" error={loadError} onRetry={retryAll} />
      )}

      {!canSeeProfile && (
        <Alert tone="info" title="Nothing to show here yet">
          Your role does not include seeing the institute profile. Ask your institute administrator
          if you think that is wrong.
        </Alert>
      )}

      {isBusy && canSeeProfile && (
        <div className="card p-6 rounded-3xl">
          <SkeletonLines count={6} />
        </div>
      )}

      {institute && !isBusy && (
        <>
          {institute.status === 'PENDING_SETUP' && (
            <Alert tone="warning" title="Your institute is still being set up">
              Check the details below and correct anything that is wrong. Once your classes and
              staff are in, the office will switch the institute to active.
            </Alert>
          )}

          {/* Top Section: Hero Photographic Card (8 cols) + Today at a glance (4 cols) */}
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-12 items-stretch relative">
            {/* Left: Photographic Banner Card (Section 1: Cinematic Zoom-In + Shimmer Light Sweep) */}
            <div className="lg:col-span-8 relative overflow-hidden rounded-2xl sm:rounded-3xl bg-cover bg-center shadow-md min-h-[240px] sm:min-h-[280px] flex flex-col justify-between p-4 sm:p-8 animate-hero-zoom delay-50 group"
                 style={{ backgroundImage: `url('/assets/schooldashboard.png')` }}>
              {/* Balanced Medium Gradient Overlay for Text Readability */}
              <div className="absolute inset-0 bg-gradient-to-r from-[#173059]/90 via-[#1b3d68]/70 to-[#173059]/30 backdrop-blur-[0.5px] pointer-events-none" />

              {/* Shimmer Light Beam Sweep */}
              <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent animate-shimmer pointer-events-none" />

              <div className="relative z-10">
                <div className="inline-flex items-center gap-2 px-3.5 py-1 rounded-full text-xs font-semibold bg-white/20 text-white backdrop-blur-md border border-white/30 shadow-xs mb-3">
                  {/* The institute's own session label. There is no term in the
                      data model, so the badge names the year it has. */}
                  <span>Session · {overview?.academicYear.name ?? NOT_MINE}</span>
                </div>
                <h1 className="text-xl sm:text-3xl lg:text-4xl font-extrabold text-white tracking-tight drop-shadow-sm">
                  Welcome back to {institute.displayName}
                </h1>
                <p className="mt-2.5 sm:mt-4 text-xs sm:text-[14px] text-slate-100/90 font-medium max-w-xl leading-relaxed drop-shadow-sm">
                  {heroSummary(overview)}
                </p>
              </div>

              <div className="relative z-10 mt-5 sm:mt-6 flex flex-wrap items-center gap-2.5 sm:gap-3">
                <Link
                  to="/admin/institute"
                  className="bg-white text-[#173059] hover:bg-slate-100 font-extrabold text-xs sm:text-[14px] px-5 sm:px-6 py-2.5 rounded-full shadow-md transition-all duration-200 cursor-pointer inline-flex items-center justify-center gap-2 w-full sm:w-auto"
                >
                  <span>View institute profile</span>
                </Link>
                <Link
                  to="/admin/roles"
                  className="bg-white/15 hover:bg-white/25 text-white border border-white/30 font-semibold text-xs sm:text-[14px] px-5 sm:px-6 py-2.5 rounded-full backdrop-blur-md transition-all duration-200 cursor-pointer inline-flex items-center justify-center gap-2 w-full sm:w-auto"
                >
                  <span>Roles</span>
                </Link>
              </div>
            </div>

            {/* Right: Important Updates Card (Section 2: Smooth Top Drop-Down + Elevated Lift) */}
            <div className="lg:col-span-4 rounded-2xl sm:rounded-[28px] p-4 sm:p-6 bg-white dark:bg-[#111c2e] border-0 -translate-y-1.5 shadow-[0_14px_30px_-8px_rgba(37,99,235,0.12)] dark:shadow-[0_14px_30px_-8px_rgba(37,99,235,0.22)] flex flex-col animate-slide-down delay-180 transition-transform duration-300">
              <div>
                <div className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-2.5">
                    <span className="grid h-8 w-8 shrink-0 place-items-center rounded-xl bg-slate-100 dark:bg-slate-800 text-ink">
                      <Bell className="h-4 w-4" />
                    </span>
                    <h2 className="text-base sm:text-lg font-extrabold text-ink tracking-tight">Important updates</h2>
                  </div>
                  {updates.length > 0 && (
                    <Link to="/admin/notices" className="text-sm font-bold text-emerald-600 hover:text-emerald-700 dark:text-emerald-400 hover:underline cursor-pointer">
                      View all
                    </Link>
                  )}
                </div>
              </div>

              {updates.length === 0 ? (
                <div className="flex-1 flex flex-col items-center justify-center text-center py-6 px-4 my-auto">
                  <span className="grid h-12 w-12 place-items-center rounded-2xl bg-slate-100 dark:bg-slate-800/90 text-slate-400 dark:text-slate-500 mb-2.5 shadow-2xs">
                    <BellOff className="h-5 w-5" />
                  </span>
                  <p className="text-sm font-bold text-ink">No updates yet</p>
                  <p className="text-xs text-ink-3 max-w-[260px] mt-1 font-medium leading-relaxed">
                    Important notices and announcements will appear here once published.
                  </p>
                </div>
              ) : (
                <div className="mt-3.5 flex flex-col gap-1">
                  {updates.map((update, index) => {
                    return (
                      <Fragment key={update.uuid}>
                        {/* Between the rows, never after the last one. */}
                        {index > 0 && (
                          <div className="w-full overflow-hidden my-1.5 opacity-80 h-1 flex items-center shrink-0">
                            <svg className="w-full h-1" preserveAspectRatio="none" viewBox="0 0 100 2">
                              <line
                                x1="0"
                                y1="1"
                                x2="200"
                                y2="1"
                                stroke="#6ee7b7"
                                strokeWidth="2"
                                strokeDasharray="4 4"
                                vectorEffect="non-scaling-stroke"
                                className="animate-svg-dash"
                              />
                            </svg>
                          </div>
                        )}

                        <ImportantUpdateRow update={update} />
                      </Fragment>
                    );
                  })}
                </div>
              )}
            </div>
          </div>

          {/* Quick Actions / Quick Links Row */}
         
          {/* Bottom Section: 4 Stat Cards Row (Section 3: Bouncy Spring Pop-In Cascade) */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 p-1 -m-1">
            {/* Card 1: STUDENTS PRESENT */}
            <MetricTile to={studentsTo} label={"Students present today — open the student register"} className="group rounded-[26px] px-4 py-3.5 bg-gradient-to-br from-[#eff6ff] via-white to-[#dbeafe]/40 dark:from-[#111c2e] dark:via-[#111c2e] dark:to-[#1e3a8a]/30 border border-[#bfdbfe] dark:border-[#1e40af]/40 shadow-xs relative flex flex-col justify-between hover:shadow-lg hover:shadow-blue-500/10 hover:-translate-y-1 transition-[transform,box-shadow] duration-200 ease-out animate-card-pop delay-300">
              <div>
                <div className="flex items-center justify-between gap-2">
                  <span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-gradient-to-tr from-[#2563eb] to-[#38bdf8] text-white shadow-md shadow-blue-500/25 group-hover:scale-110 transition-transform duration-300">
                    <Users className="h-5 w-5" />
                  </span>
                </div>
                <div className="mt-2.5">
                  <span className="text-[14px] font-black text-ink-3  font-semibold tracking-wide block">Students Present</span>
                  {countedOn && (
                    <span className="mt-0.5 flex items-center gap-1 text-[11px] font-bold text-[#1d4ed8] dark:text-blue-300">
                      <Calendar className="h-3 w-3" aria-hidden />
                      {countedOn}
                    </span>
                  )}
                  <p className="mt-1 text-2xl font-black text-ink tracking-tight">{students ? formatNumber(students.present) : NOT_MINE} <span className="text-[14px] font-bold text-ink-3">/ {students ? formatNumber(students.total) : NOT_MINE}</span></p>
                </div>
              </div>
              <div className="mt-2">
                <p className="flex items-center justify-between text-[11px] font-semibold text-ink-3 mb-1.5">
                  <span>Attendance rate</span>
                  <span className="text-xs font-black text-[#1d4ed8] dark:text-blue-300">{students ? `${students.attendanceRate}%` : NOT_MINE}</span>
                </p>
                <div className="h-1.5 w-full rounded-full bg-[#dbeafe] dark:bg-slate-800 overflow-hidden">
                  {/* The fill is the only inline style on this screen, and it
                      cannot be a utility class: `w-[${n}%]` is a class the
                      compiler never sees and so never generates. Same reason
                      `components/ui/ProgressBar` gives. */}
                  <div className="h-full bg-gradient-to-r from-[#2563eb] to-[#38bdf8] rounded-full" style={{ width: `${barWidth(students?.attendanceRate)}%` }} />
                </div>
              </div>
            </MetricTile>

            {/* Card 2: TEACHERS ON DUTY */}
            <MetricTile to={onDutyTo} label={"Teachers on duty today — open staff punch-ins"} className="group rounded-[26px] px-4 py-3.5 bg-gradient-to-br from-[#ecfdf5] via-white to-[#a7f3d0]/40 dark:from-[#111c2e] dark:via-[#111c2e] dark:to-[#065f46]/30 border border-[#a7f3d0] dark:border-[#065f46]/40 shadow-xs relative flex flex-col justify-between hover:shadow-lg hover:shadow-emerald-500/10 hover:-translate-y-1 transition-[transform,box-shadow] duration-200 ease-out animate-card-pop delay-400">
              <div>
                <div className="flex items-center justify-between gap-2">
                  <span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-gradient-to-tr from-[#059669] to-[#2dd4bf] text-white shadow-md shadow-emerald-500/25 group-hover:scale-110 transition-transform duration-300">
                    <GraduationCap className="h-5 w-5" />
                  </span>
                  <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[10px] font-black bg-[#ecfdf5] text-[#047857] dark:bg-emerald-950/90 dark:text-emerald-300 border border-[#a7f3d0] dark:border-emerald-800">
                    <span className="h-1.5 w-1.5 rounded-full bg-[#10b981] animate-pulse" />
                    ON DUTY
                  </span>
                </div>
                <div className="mt-2.5">
                  <span className="text-[11px] font-black text-ink-3 uppercase tracking-wider block">TEACHERS ON DUTY</span>
                  {countedOn && (
                    <span className="mt-0.5 flex items-center gap-1 text-[11px] font-bold text-[#047857] dark:text-emerald-300">
                      <Calendar className="h-3 w-3" aria-hidden />
                      {countedOn}
                    </span>
                  )}
                  <p className="mt-1 text-2xl font-black text-ink tracking-tight">{staff ? formatNumber(staff.onDuty) : NOT_MINE} <span className="text-xs font-bold text-ink-3">/ {staff ? formatNumber(staff.teachers) : NOT_MINE}</span></p>
                </div>
              </div>
              <div className="mt-2">
                <p className="flex items-center justify-between text-[11px] font-semibold text-ink-3 mb-1.5">
                  <span>Attendance rate</span>
                  <span className="text-xs font-black text-[#047857] dark:text-emerald-300">{staff ? `${staff.onDutyRate}%` : NOT_MINE}</span>
                </p>
                <div className="h-1.5 w-full rounded-full bg-[#a7f3d0] dark:bg-slate-800 overflow-hidden">
                  <div className="h-full bg-gradient-to-r from-[#059669] to-[#2dd4bf] rounded-full" style={{ width: `${barWidth(staff?.onDutyRate)}%` }} />
                </div>
              </div>
            </MetricTile>

            {/* Card 3: ACTIVE STAFF */}
            <MetricTile to={staffTo} label={"Active staff — open the staff list"} className="group rounded-[26px] px-4 py-3.5 bg-gradient-to-br from-[#faf5ff] via-white to-[#f3e8ff]/40 dark:from-[#111c2e] dark:via-[#111c2e] dark:to-[#581c87]/30 border border-[#e9d5ff] dark:border-[#6b21a8]/40 shadow-xs relative flex flex-col justify-between hover:shadow-lg hover:shadow-purple-500/10 hover:-translate-y-1 transition-[transform,box-shadow] duration-200 ease-out animate-card-pop delay-500">
              <div>
                <div className="flex items-center justify-between gap-2">
                  <span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-gradient-to-tr from-[#9333ea] to-[#818cf8] text-white shadow-md shadow-purple-500/25 group-hover:scale-110 transition-transform duration-300">
                    <Contact className="h-5 w-5" />
                  </span>
                  <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[10px] font-black bg-[#faf5ff] text-[#7e22ce] dark:bg-purple-950/90 dark:text-purple-300 border border-[#e9d5ff] dark:border-purple-800">
                    {staff ? `${formatNumber(staff.total)} ACTIVE` : NOT_MINE}
                  </span>
                </div>
                <div className="mt-2.5">
                  <span className="text-[11px] font-black text-ink-3 uppercase tracking-wider block">ACTIVE STAFF</span>
                  <p className="mt-1 text-2xl font-black text-ink tracking-tight">{staff ? formatNumber(staff.total) : NOT_MINE} <span className="text-xs font-bold text-ink-3">Staff</span></p>
                </div>
              </div>
              <div className="mt-2">
                <p className="text-[11px] font-semibold text-ink-3 mb-1.5">Teachers & admins & support staff</p>
                <div className="h-1.5 w-full rounded-full bg-[#f3e8ff] dark:bg-slate-800 overflow-hidden">
                  <div className="h-full bg-gradient-to-r from-[#9333ea] to-[#818cf8] rounded-full w-full" />
                </div>
              </div>
            </MetricTile>

            {/* Card 5: FEES COLLECTED */}
            <MetricTile to={feesTo} label={"Fees collected this session — open the fee counter"} className="group rounded-[26px] px-4 py-3.5 bg-gradient-to-br from-[#f0fdfa] via-white to-[#ccfbf1]/40 dark:from-[#111c2e] dark:via-[#111c2e] dark:to-[#134e4a]/30 border border-[#99f6e4] dark:border-[#115e59]/40 shadow-xs relative flex flex-col justify-between hover:shadow-lg hover:shadow-teal-500/10 hover:-translate-y-1 transition-[transform,box-shadow] duration-200 ease-out animate-card-pop delay-700">
              <div>
                <div className="flex items-center justify-between gap-2">
                  <span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-gradient-to-tr from-[#0d9488] to-[#22d3ee] text-white shadow-md shadow-teal-500/25 group-hover:scale-110 transition-transform duration-300">
                    <CreditCard className="h-5 w-5" />
                  </span>
                  <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[10px] font-black bg-[#f0fdfa] text-[#0f766e] dark:bg-teal-950/90 dark:text-teal-300 border border-[#99f6e4] dark:border-teal-800">
                    {fees ? `${fees.collectionRate}% COLLECTED` : NOT_MINE}
                  </span>
                </div>
                <div className="mt-2.5">
                  <span className="text-[11px] font-black text-ink-3 uppercase tracking-wider block">FEES COLLECTED</span>
                  {/* Rupees, from `formatCurrency` — this product is sold to
                      Indian institutes and money is never formatted by hand. */}
                  <p className="mt-1 text-2xl font-black text-ink tracking-tight">{fees ? formatCurrency(fees.collected) : NOT_MINE}</p>
                </div>
              </div>
              <div className="mt-2">
                <p className="text-[11px] font-semibold text-ink-3 mb-1.5">Collected of fees payable this session</p>
                <div className="h-1.5 w-full rounded-full bg-[#ccfbf1] dark:bg-slate-800 overflow-hidden">
                  <div className="h-full bg-gradient-to-r from-[#0d9488] to-[#22d3ee] rounded-full" style={{ width: `${barWidth(fees?.collectionRate)}%` }} />
                </div>
              </div>
            </MetricTile>
          </div>

          <div className="animate-slide-left delay-800">
            <h2 className="text-base sm:text-lg font-black text-ink tracking-tight mb-3">Quick actions</h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2.5 sm:gap-3.5">
              <Link
                to="/admin/students"
                className="bg-[#ecfdf5] dark:bg-emerald-950/50 text-[#059669] dark:text-emerald-300 border border-[#a7f3d0] dark:border-emerald-800/60 shadow-xs hover:shadow-md hover:-translate-y-0.5 transition-all duration-200 cursor-pointer p-3 sm:p-3.5 rounded-2xl font-black text-xs sm:text-sm flex items-center justify-center gap-2"
              >
                <UserPlus className="h-4 w-4 shrink-0 text-[#059669] dark:text-emerald-300" />
                <span className="truncate">Students</span>
              </Link>
              <Link
                to="/admin/staff"
                className="bg-[#eff6ff] dark:bg-blue-950/50 text-[#2563eb] dark:text-blue-300 border border-[#bfdbfe] dark:border-blue-800/60 shadow-xs hover:shadow-md hover:-translate-y-0.5 transition-all duration-200 cursor-pointer p-3 sm:p-3.5 rounded-2xl font-black text-xs sm:text-sm flex items-center justify-center gap-2"
              >
                <Contact className="h-4 w-4 shrink-0 text-[#2563eb] dark:text-blue-300" />
                <span className="truncate">Staff & Teachers</span>
              </Link>
              <Link
                to="/admin/classes"
                className="bg-[#fdf4ff] dark:bg-fuchsia-950/50 text-[#c026d3] dark:text-fuchsia-300 border border-[#f5d0fe] dark:border-fuchsia-800/60 shadow-xs hover:shadow-md hover:-translate-y-0.5 transition-all duration-200 cursor-pointer p-3 sm:p-3.5 rounded-2xl font-black text-xs sm:text-sm flex items-center justify-center gap-2"
              >
                <Layers className="h-4 w-4 shrink-0 text-[#c026d3] dark:text-fuchsia-300" />
                <span className="truncate">Classes</span>
              </Link>
              <Link
                to="/admin/fees"
                className="bg-[#fff7ed] dark:bg-amber-950/50 text-[#ea580c] dark:text-amber-300 border border-[#fed7aa] dark:border-amber-800/60 shadow-xs hover:shadow-md hover:-translate-y-0.5 transition-all duration-200 cursor-pointer p-3 sm:p-3.5 rounded-2xl font-black text-xs sm:text-sm flex items-center justify-center gap-2"
              >
                <CreditCard className="h-4 w-4 shrink-0 text-[#ea580c] dark:text-amber-300" />
                {/* Named for where it goes, like the tiles beside it: the Fees page,
                    not the collect form — a fee is taken from a student's row there. */}
                <span className="truncate">Fees</span>
              </Link>
              <Link
                to="/admin/notices"
                className="bg-[#faf5ff] dark:bg-purple-950/50 text-[#9333ea] dark:text-purple-300 border border-[#e9d5ff] dark:border-purple-800/60 shadow-xs hover:shadow-md hover:-translate-y-0.5 transition-all duration-200 cursor-pointer p-3 sm:p-3.5 rounded-2xl font-black text-xs sm:text-sm flex items-center justify-center gap-2"
              >
                <Megaphone className="h-4 w-4 shrink-0 text-[#9333ea] dark:text-purple-300" />
                <span className="truncate">Notice Board</span>
              </Link>
              <Link
                to="/admin/timetable"
                className="bg-[#f0fdfa] dark:bg-teal-950/50 text-[#0d9488] dark:text-teal-300 border border-[#99f6e4] dark:border-teal-800/60 shadow-xs hover:shadow-md hover:-translate-y-0.5 transition-all duration-200 cursor-pointer p-3 sm:p-3.5 rounded-2xl font-black text-xs sm:text-sm flex items-center justify-center gap-2"
              >
                <CalendarClock className="h-4 w-4 shrink-0 text-[#0d9488] dark:text-teal-300" />
                <span className="truncate">Timetable</span>
              </Link>
            </div>
          </div>

          {/* Detailed Info Section: 100% Exact Match to Reference Image */}
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-2 items-start">
            {/* Left: Institute Identity */}
            <section className="rounded-2xl sm:rounded-[28px] p-4 sm:p-7 border border-slate-200/90 dark:border-slate-800/80 bg-white dark:bg-[#111c2e] shadow-xs animate-slide-left delay-900">
              <header className="mb-4 flex items-center justify-between border-b border-slate-200/80 dark:border-slate-800/80 pb-3.5">
                <span className="flex items-center gap-3">
                  <span className="grid h-11 w-11 place-items-center rounded-2xl bg-blue-600 text-white shadow-xs">
                    <Building2 className="h-5 w-5" />
                  </span>
                  <div>
                    <h2 className="text-base sm:text-lg font-extrabold text-ink tracking-tight">Institute Overview</h2>
                    <p className="text-xs font-medium text-ink-3">Core parameters & structure</p>
                  </div>
                </span>
                {/* Only a school that is actually running gets the green pulse; a
                    suspended or archived account must not look healthy. */}
                {institute.status === 'ACTIVE' ? (
                  <span className="inline-flex items-center gap-1.5 px-2.5 sm:px-3 py-1 rounded-full text-xs font-extrabold uppercase bg-[#dcfce7] text-[#16a34a] border border-[#bbf7d0]">
                    <span className="h-2 w-2 rounded-full bg-[#16a34a] animate-pulse" />
                    {SCHOOL_STATUS_LABEL[institute.status]}
                  </span>
                ) : (
                  <Badge tone={SCHOOL_STATUS_TONE[institute.status]} withDot>
                    {SCHOOL_STATUS_LABEL[institute.status]}
                  </Badge>
                )}
              </header>

              <div className="flex flex-col gap-1">
                <Row label="Name" value={institute.displayName} icon={<Building2 className="h-3.5 w-3.5 text-blue-500" />} />
                <Row label="Registered As" value={institute.legalName} icon={<ShieldCheck className="h-3.5 w-3.5 text-blue-500" />} />
                <Row label="Board" value={boardLabel(institute.board)} icon={<Award className="h-3.5 w-3.5 text-blue-500" />} valueColor="text-blue-600 dark:text-blue-400" />
                <Row label="Level" value={instituteLevelLabel(institute)} icon={<GraduationCap className="h-3.5 w-3.5 text-purple-500" />} valueColor="text-purple-600 dark:text-purple-400" />
                <Row label="Classes" value={`${institute.lowestClass} – ${institute.highestClass}`} icon={<Layers className="h-3.5 w-3.5 text-emerald-500" />} valueColor="text-emerald-600 dark:text-emerald-400" />
                <Row label="On Platform Since" value={formatDate(institute.createdAt)} icon={<Calendar className="h-3.5 w-3.5 text-amber-500" />} valueColor="text-amber-600 dark:text-amber-400" />
              </div>
            </section>

            {/* Right: Campus Address & Contact (Section 6: 3D Perspective Flip In) */}
            <section className="rounded-2xl sm:rounded-[28px] p-4 sm:p-7 border border-slate-200/90 dark:border-slate-800/80 bg-white dark:bg-[#111c2e] shadow-xs animate-flip-in delay-1000">
              <header className="mb-4 flex items-center gap-3 border-b border-slate-200/80 dark:border-slate-800/80 pb-3.5">
                <span className="grid h-11 w-11 place-items-center rounded-2xl bg-indigo-600 text-white shadow-xs">
                  <MapPin className="h-5 w-5" />
                </span>
                <div>
                  <h2 className="text-base sm:text-lg font-extrabold text-ink tracking-tight">Campus Location & Contact</h2>
                  <p className="text-xs font-medium text-ink-3">Official address & management</p>
                </div>
              </header>

              <div className="rounded-2xl sm:rounded-3xl border border-[#e9d5ff] dark:border-[#581c87]/40 bg-gradient-to-r from-[#f3ebff] via-[#faf5ff] to-[#f3ebff] dark:from-[#2e1065]/30 dark:to-[#1e1b4b]/30 p-3.5 sm:p-5 mb-4 shadow-2xs flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-extrabold uppercase tracking-wider text-purple-600 dark:text-purple-400 flex items-center gap-1.5">
                    <MapPin className="h-4 w-4 text-purple-500" /> CAMPUS ADDRESS
                  </p>
                  <p className="text-[12px] font-extrabold leading-relaxed text-ink mt-1.5">
                    {[
                      institute.addressLine1,
                      institute.addressLine2,
                      institute.locality,
                      institute.city,
                    ]
                      .filter(Boolean)
                      .join(', ')}
                    <br />
                    <span className="text-ink-2 font-bold">{institute.state} — {institute.pincode}</span>
                  </p>
                  <a
                    href={googleMapsSearchUrl([
                      institute.addressLine1,
                      institute.locality,
                      institute.city,
                      institute.state,
                      institute.pincode,
                    ])}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-1.5 text-xs font-bold text-purple-600 hover:text-purple-700 dark:text-purple-400 mt-2 hover:underline"
                  >
                    <span>Open in Google Maps</span>
                    <span className="text-[10px]">↗</span>
                  </a>
                </div>

                {/* Embedded Interactive Google Map Preview (Wider & Overlay Fully Hidden) */}
                <div className="w-full sm:w-[220px] h-[120px] sm:h-[105px] rounded-2xl overflow-hidden border border-purple-300 dark:border-purple-800 shadow-sm shrink-0 relative group">
                  <iframe
                    title="Campus Location Map"
                    style={{ border: 0 }}
                    loading="lazy"
                    allowFullScreen
                    src={`https://maps.google.com/maps?q=${encodeURIComponent(
                      [institute.addressLine1, institute.locality, institute.city, institute.state].filter(Boolean).join(',')
                    )}&t=&z=14&ie=UTF8&iwloc=&output=embed`}
                    className="w-[135%] h-[155%] -top-10 -left-6 absolute object-cover filter contrast-[1.05] brightness-[0.98] pointer-events-none"
                  />
                </div>
              </div>

              <div className="flex flex-col gap-2">
                <ContactRow label="Phone" value={institute.phone} icon={<Phone className="h-4 w-4 text-emerald-500" />} />
                <ContactRow label="Email" value={institute.email} icon={<Mail className="h-4 w-4 text-sky-500" />} />
                <ContactRow label="Principal" value={institute.principalName} icon={<UserCheck className="h-4 w-4 text-purple-500" />} />
              </div>
            </section>
          </div>
        </>
      )}
    </div>
  );
}

function Row({
  label,
  value,
  icon,
  valueColor,
}: {
  label: string;
  value: string;
  icon?: React.ReactNode;
  valueColor?: string;
}) {
  return (
    <div className="grid grid-cols-[1.2fr_16px_1.4fr] sm:grid-cols-[1.1fr_20px_1.4fr] items-center py-2.5 sm:py-3 border-b border-dashed border-slate-200 dark:border-slate-800 last:border-0 hover:bg-slate-50/50 dark:hover:bg-slate-800/30 px-2 rounded-xl transition-colors">
      <div className="flex items-center gap-2 sm:gap-2.5 min-w-0">
        <span className="grid h-6.5 w-6.5 shrink-0 place-items-center rounded-full bg-slate-100 dark:bg-slate-800 text-ink-2 text-xs">
          {icon}
        </span>
        <span className="text-xs sm:text-[13px] font-bold text-ink-2 truncate">{label}</span>
      </div>

      <div className="text-center font-bold text-ink-3 text-xs sm:text-[13px]">:</div>

      <div className="text-left min-w-0 pl-1">
        <span className={`text-xs sm:text-[13px] font-extrabold block truncate ${valueColor ?? 'text-ink'}`}>
          {value}
        </span>
      </div>
    </div>
  );
}

function ContactRow({ label, value, icon }: { label: string; value: string; icon?: React.ReactNode }) {
  return (
    <div className="flex items-center justify-between gap-2 sm:gap-3 px-3.5 py-2.5 rounded-2xl border border-slate-200/90 dark:border-slate-800/80 bg-white dark:bg-[#111c2e] hover:bg-slate-50/50 dark:hover:bg-slate-800/30 transition-colors min-w-0">
      <div className="flex items-center gap-2.5 sm:gap-3 shrink-0">
        <span className="grid h-7 w-7 place-items-center rounded-xl bg-slate-100 dark:bg-slate-800 text-ink-2">
          {icon}
        </span>
        <span className="text-xs sm:text-[13px] font-bold text-ink-2">{label}</span>
      </div>
      <span className="text-xs sm:text-[13px] font-extrabold text-ink truncate min-w-0 text-right" title={value}>
        {value}
      </span>
    </div>
  );
}

